import { cn } from '@/lib/utils';
import {
  echoScoreDisplay,
} from '@/config/indexThresholds';
import {
  recommendationTextClass,
  scriptDisplayStatus,
} from '@/config/recommendation';
import { formatScriptId } from '@/utils/formatIndexId';
import type { WorkflowScriptListItem } from '@/types/workflow';

type AuditLogFeedProps = {
  scripts: WorkflowScriptListItem[];
  maxItems?: number;
  className?: string;
};

function parseLogDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;
  try {
    if (
      typeof dateValue === 'object' &&
      dateValue !== null &&
      'toDate' in dateValue &&
      typeof (dateValue as { toDate: () => Date }).toDate === 'function'
    ) {
      return (dateValue as { toDate: () => Date }).toDate();
    }
    const d = new Date(String(dateValue));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatLogDateTime(dateValue: unknown): string {
  const d = parseLogDate(dateValue);
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function auditTimestamp(script: WorkflowScriptListItem): unknown {
  return script.created_at ?? script.updated_at;
}

export default function AuditLogFeed({ scripts, maxItems = 8, className }: AuditLogFeedProps) {
  const entries = [...scripts]
    .sort((a, b) => {
      const ta = String(a.created_at ?? '');
      const tb = String(b.created_at ?? '');
      return tb.localeCompare(ta);
    })
    .slice(0, maxItems);

  if (!entries.length) {
    return (
      <div className={cn('ni-panel p-4', className)}>
        <p className="ni-label mb-3">Audit log</p>
        <p className="text-sm text-white/40 font-sans">No audit events for this mandate.</p>
      </div>
    );
  }

  return (
    <div className={cn('ni-panel p-4', className)}>
      <div className="flex items-center justify-between mb-3">
        <p className="ni-label">Audit log</p>
        <span className="text-[10px] font-mono text-index-cleared uppercase tracking-wider">
          Live
        </span>
      </div>
      <ul className="space-y-2">
        {entries.map((script) => {
          const id = formatScriptId(script.analysis_session_id || script.session_id);
          const status = scriptDisplayStatus(script);
          const score = echoScoreDisplay(script.overall_score);
          return (
            <li
              key={script.session_id}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs font-mono text-white/55"
            >
              <span className="text-white/30 tabular-nums shrink-0">
                {formatLogDateTime(auditTimestamp(script))}
              </span>
              <span className="text-white/70">{id}</span>
              <span className={cn(recommendationTextClass(status))}>{status}</span>
              {score != null && (
                <span className="text-white/45">· {score.toFixed(1)}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
